<form [formGroup]="formGroup">
  <div class="row">
    <div class="guide-section__title" style="padding-top: 40px">
      General settings
      <div class="guide-section__subtext">Basic setup for this delivery option</div>
    </div>

    <div class="content__general-settings general-settings-v2-container">
      <pdq-heading class="title" element="h5">General settings</pdq-heading>
      <div class="flex-row">
        <pdq-input
          label="Internal name (hidden from customers)"
          width="400"
          formControlName="internalName"
          placeholder="Set internal name">
        </pdq-input>

        @if (shop.metadata.setCheckoutServiceCodeBy === 'serviceCodeInput') {
          <pdq-input
            label="Shipping code (Customers won’t see this)"
            width="400"
            formControlName="serviceCodeInput"
            placeholder="Set custom service code">
          </pdq-input>
        }
      </div>

      <div class="flex-row">
        <div class="delivery-method-container">
          <rich-text-editor
            label="Name of delivery method"
            placeholder="Example: Next Day Delivery"
            [control]="formGroup.get('name')"
            [invalidMsg]="formGroup.get('name')?.hasError('required') ? 'Name of delivery method is required' : null"
            [disabledFormats]="['italic', 'bold', 'underline', 'strike', 'align', 'list', 'header']"
            [config]="deliveryMethodConfig">
          </rich-text-editor>
          @if (showDeliveryPromiseHelper) {
            <p class="delivery-promise-helper">**Delivery promise would show only in the name of the method</p>
          } @else {
            <p class="delivery-promise-helper">**Dynamic fields available. Type {{ '{' }} to see options.</p>
          }
          @if (isProposedNameUnmapped) {
            <pdq-banner
              style="margin: 8px 0 0 0"
              [closable]="false"
              variant="attention"
              bannerTitle="Name not yet mapped in Global-e"
              content="This name isn’t in Global-e’s mapping yet — the method won’t render until Global-e adds it. Notify Global-e before go-live."></pdq-banner>
          }
        </div>
        <rich-text-editor
          [label]="notesLabel"
          placeholder="Example: Free for orders above 100$"
          [control]="formGroup.get('notes')"
          [disabledFormats]="['italic', 'bold', 'underline', 'strike', 'align', 'list', 'header']"
          [invalidMsg]="notesInvalidMsg">
        </rich-text-editor>
      </div>

      @if (deliveryMethodSectionOptions.length) {
        <div class="row">
          <pdq-dropdown
            title="Section"
            width="400"
            [options]="deliveryMethodSectionOptions"
            [defaultOption]="selectedDeliveryMethodSectionOption"
            formControlName="deliveryMethodSectionId"></pdq-dropdown>
        </div>
      }

      @if (!isPickUpShippingMethod) {
        <pdq-heading element="h5">Shipping Rates</pdq-heading>

        <div class="row">
          <pdq-dropdown
            [title]="'Currency'"
            [options]="currencyUnitsOptions"
            (onChange)="onMethodCurrencyChange($event)"
            [defaultOption]="{ label: shopCurrency, value: shopCurrency }"
            formControlName="methodCurrency"></pdq-dropdown>
        </div>

        @if (isUsingRateCard) {
          <pdq-banner
            variant="regular"
            [closable]="false"
            bannerTitle="Rate card is enabled"
            content="You can only set a rate card for this shipping method. Please contact support if you want to disable it.">
          </pdq-banner>
        }

        @if (!isLiveRateShippingMethod) {
          <div class="shipping-rates-container">
            <!-- Main rate item -->
            <div class="rate-item">
              <pdq-card color="white" variant="flat" class="rate-card">
                <div class="rate-row">
                  <pdq-input-arrows
                    [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                    tooltipIconColor="var(--warning-default)"
                    width="275"
                    widthSize="auto"
                    formControlName="rate"
                    [disabled]="!!runningABTest || isUsingRateCard"
                    placeholder="Rate (enter 0 for free)"
                    [symbol]="shippingMethodCurrency$ | async | currencySign"
                    label="Shipping rate per delivery"
                    [invalid]="formGroup.get('rate')?.hasError('required') || formGroup.get('rate')?.hasError('min')"
                    [required]="true"
                    precision="2"
                    [min]="0">
                  </pdq-input-arrows>
                  <pdq-input-arrows
                    [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                    tooltipIconColor="var(--warning-default)"
                    width="100"
                    widthSize="auto"
                    formControlName="minOrderPriceRange"
                    [disabled]="!!runningABTest || isUsingRateCard"
                    [symbol]="shippingMethodCurrency$ | async | currencySign"
                    label="Minimum price"
                    [required]="true"
                    precision="2"
                    [min]="0">
                  </pdq-input-arrows>
                  <pdq-input-arrows
                    [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                    tooltipIconColor="var(--warning-default)"
                    width="100"
                    widthSize="auto"
                    [disabled]="!!runningABTest || isUsingRateCard"
                    formControlName="maxOrderPriceRange"
                    placeholder="No limit"
                    [symbol]="shippingMethodCurrency$ | async | currencySign"
                    label="Maximum price"
                    [required]="false"
                    precision="2"
                    [min]="0">
                  </pdq-input-arrows>
                </div>

                <!-- Discount Section (only shown for methods that already have a discount configured) -->
                @if (!isLiveRateETAFixedRate && existingShippingHasDiscount) {
                  <div class="discount-section">
                    <pdq-checkbox
                      [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                      formControlName="hasDiscount"
                      label="Add Discount"
                      secondaryLabel="The discount is applied after the shipping rate and additional costs have been added"
                      [disabled]="!!runningABTest || isUsingRateCard">
                    </pdq-checkbox>

                    @if (formGroup.get('hasDiscount')?.value) {
                      <div class="discount-controls">
                        <pdq-dropdown
                          [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                          [options]="discountTypeOptions"
                          formControlName="discountType"
                          label="Discount Type"
                          [disabled]="!!runningABTest || isUsingRateCard"
                          (selectionChange)="onDiscountTypeChange($event, true)">
                        </pdq-dropdown>

                        <pdq-input-arrows
                          [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                          tooltipIconColor="var(--warning-default)"
                          width="200"
                          widthSize="auto"
                          formControlName="discountAmount"
                          [disabled]="!!runningABTest || isUsingRateCard"
                          [placeholder]="getDiscountPlaceholder(formGroup.get('discountType')?.value)"
                          [symbol]="getDiscountSymbol(formGroup.get('discountType')?.value)"
                          precision="2"
                          [min]="0"
                          [max]="formGroup.get('discountType')?.value === 'percentage' ? 100 : undefined"
                          [invalid]="
                            formGroup.get('discountAmount')?.hasError('required') ||
                            formGroup.get('discountAmount')?.hasError('min')
                          ">
                        </pdq-input-arrows>
                      </div>
                    }
                  </div>
                }
              </pdq-card>
              <div class="rate-actions">
                <pdq-button
                  [disabled]="!!runningABTest || isUsingRateCard"
                  icon="circle-plus"
                  iconColor="var(--on-surface-light)"
                  variant="empty"
                  size="small"
                  (onClick)="addAdditionalPriceRange()"></pdq-button>
                <pdq-button
                  [disabled]="!!runningABTest || isUsingRateCard || !canRemoveMainRate()"
                  icon="trash"
                  iconColor="var(--error-default)"
                  variant="empty"
                  size="small"
                  (onClick)="removeMainRate()"></pdq-button>
              </div>
            </div>

            <!-- Additional rate items -->
            <div formArrayName="additionalPriceRanges">
              @for (additionalPriceRage of additionalPriceRanges.controls; track additionalPriceRage; let i = $index) {
                <div class="rate-item">
                  <pdq-card color="white" variant="flat" class="rate-card">
                    <div [formGroupName]="i">
                      <div class="rate-row">
                        <pdq-input-arrows
                          [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                          tooltipIconColor="var(--warning-default)"
                          width="275"
                          widthSize="auto"
                          formControlName="rate"
                          [disabled]="!!runningABTest || isUsingRateCard"
                          placeholder="Rate (enter 0 for free)"
                          [symbol]="shippingMethodCurrency$ | async | currencySign"
                          [min]="0"
                          precision="2"
                          [required]="false"
                          [invalid]="
                            additionalPriceRanges.at(i)?.get('rate')?.hasError('required') ||
                            additionalPriceRanges.at(i)?.get('rate')?.hasError('min')
                          "
                          label="Shipping rate per delivery">
                        </pdq-input-arrows>
                        <pdq-input-arrows
                          [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                          tooltipIconColor="var(--warning-default)"
                          width="100"
                          widthSize="auto"
                          formControlName="minOrderPriceRange"
                          [disabled]="!!runningABTest || isUsingRateCard"
                          [symbol]="shippingMethodCurrency$ | async | currencySign"
                          label="Minimum price"
                          [required]="true"
                          precision="2"
                          [min]="0">
                        </pdq-input-arrows>
                        <pdq-input-arrows
                          [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                          tooltipIconColor="var(--warning-default)"
                          width="100"
                          widthSize="auto"
                          [disabled]="!!runningABTest || isUsingRateCard"
                          formControlName="maxOrderPriceRange"
                          placeholder="No limit"
                          [symbol]="shippingMethodCurrency$ | async | currencySign"
                          label="Maximum price"
                          [required]="false"
                          precision="2"
                          [min]="0">
                        </pdq-input-arrows>
                      </div>

                      <!-- Discount Section (only shown for methods that already have a discount configured) -->
                      @if (!isLiveRateETAFixedRate && existingShippingHasDiscount) {
                        <div class="discount-section">
                          <pdq-checkbox
                            [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                            formControlName="hasDiscount"
                            label="Add Discount"
                            secondaryLabel="The discount is applied after the shipping rate and additional costs have been added"
                            [disabled]="!!runningABTest || isUsingRateCard">
                          </pdq-checkbox>

                          @if (additionalPriceRanges.at(i)?.get('hasDiscount')?.value) {
                            <div class="discount-controls">
                              <pdq-dropdown
                                [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                                [options]="discountTypeOptions"
                                formControlName="discountType"
                                label="Discount Type"
                                [disabled]="!!runningABTest || isUsingRateCard"
                                (selectionChange)="onDiscountTypeChange($event, false, i)">
                              </pdq-dropdown>

                              <pdq-input-arrows
                                [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
                                tooltipIconColor="var(--warning-default)"
                                width="200"
                                widthSize="auto"
                                formControlName="discountAmount"
                                [disabled]="!!runningABTest || isUsingRateCard"
                                [placeholder]="
                                  getDiscountPlaceholder(additionalPriceRanges.at(i)?.get('discountType')?.value)
                                "
                                [symbol]="getDiscountSymbol(additionalPriceRanges.at(i)?.get('discountType')?.value)"
                                precision="2"
                                [min]="0"
                                [max]="
                                  additionalPriceRanges.at(i)?.get('discountType')?.value === 'percentage'
                                    ? 100
                                    : undefined
                                "
                                [invalid]="
                                  additionalPriceRanges.at(i)?.get('discountAmount')?.hasError('required') ||
                                  additionalPriceRanges.at(i)?.get('discountAmount')?.hasError('min')
                                ">
                              </pdq-input-arrows>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </pdq-card>
                  <div class="rate-actions">
                    <pdq-button
                      [disabled]="!!runningABTest || isUsingRateCard"
                      icon="circle-plus"
                      iconColor="var(--on-surface-light)"
                      variant="empty"
                      size="small"
                      (onClick)="addAdditionalPriceRange()"></pdq-button>
                    <pdq-button
                      [disabled]="!!runningABTest || isUsingRateCard"
                      icon="trash"
                      iconColor="var(--error-default)"
                      variant="empty"
                      size="small"
                      (onClick)="removeAdditionalPriceRange(i)"></pdq-button>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        @if (!isLiveRateShippingMethod && !isLiveRateETAFixedRate) {
          <div class="flex-row">
            <pdq-input-arrows
              width="275"
              widthSize="auto"
              formControlName="loyalCustomerPrice"
              [ngStyle]="{ 'font-size': '14px' }"
              placeholder="Price per delivery"
              [symbol]="shippingMethodCurrency$ | async | currencySign"
              label="Rate for loyal customers"
              [required]="false"
              precision="2"
              [min]="0">
            </pdq-input-arrows>
            <pdq-input
              label="Loyal customer note"
              width="313"
              formControlName="loyalCustomerDescription"
              placeholder="★ free shipping for you ★">
            </pdq-input>
          </div>
        }

        @if (!isLiveRateShippingMethod && !isLiveRateETAFixedRate) {
          <div class="flex-row">
            <pdq-input-arrows
              width="275"
              widthSize="auto"
              formControlName="estimatedAverageShippingCost"
              [ngStyle]="{ 'font-size': '14px' }"
              [symbol]="shopCurrencySign"
              label="Average shipping cost (estimate)"
              [required]="false"
              [min]="0"
              [precision]="2">
            </pdq-input-arrows>
          </div>
        }
      }

      @if (isInternationalShippingMethod) {
        <div *pdqHiddenInGlobaleMode [formGroupName]="'international'">
          <pdq-radio-group
            style="font-size: 14px"
            formControlName="taxPaymentType"
            labelSize="medium"
            label="For international shipments, determine how duties and taxes will be paid"
            (onChange)="handleTaxPaymentTypeChange($event)">
            <pdq-radio-button
              value="DDU"
              text="DDU (unpaid)"
              [checked]="formGroup.value.international.taxPaymentType === 'DDU'">
            </pdq-radio-button>
            <pdq-radio-button
              value="DDP"
              text="DDP (pre-paid)"
              [checked]="formGroup.value.international.taxPaymentType === 'DDP'"></pdq-radio-button>
          </pdq-radio-group>
          @let carrier = onSetSelectedCarrier$ | async;
          @if (carrier && formGroup.value.international?.taxPaymentType | displayDDPAdditionalConfig: carrier) {
            <div [formGroupName]="'ddpConfig'" class="checkbox-container">
              <pdq-checkbox
                formControlName="displayTaxesAndDutiesBreakdown"
                labelSize="small"
                label="Display a breakdown of duties and taxes under the shipping method">
              </pdq-checkbox>
            </div>
          }
        </div>
      }

      @if (changeDeliveryTypeSupported) {
        <div class="flex-row">
          <pdq-dropdown
            label="Delivery type"
            class="delivery-type__input"
            showSelectUnselectOptions="true"
            (onChange)="handleChangeDeliveryType($event)"
            placeholder="Select Delivery Type"
            [defaultOption]="findDefaultDeliveryType()"
            [options]="deliveryTypeOptions">
          </pdq-dropdown>
          @if (showFormErrors && !this.formGroup.get('deliveryType').valid) {
            <mat-error class="mat-error-text">*Select delivery type </mat-error>
          }
        </div>
      }

      <ng-container>
        @if (isLiveRateShippingMethod || isLiveRateETAFixedRate) {
          <div *pdqHiddenInGlobaleMode>
            <pdq-checkbox
              labelSize="small"
              formControlName="displayMethodAndServiceLevelName"
              label="Display carrier and service level names"
              secondaryLabel="Live rate service level name will be displayed (in addition to the custom name if entered)"></pdq-checkbox>
          </div>
        }

        @if (isLiveRateShippingMethod) {
          <div class="flex-row">
            <pdq-input-arrows
              [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
              tooltipIconColor="var(--warning-default)"
              width="275"
              widthSize="auto"
              formControlName="percentageOfCarrierCost"
              placeholder="For example: 100"
              symbol="%"
              [disabled]="!!runningABTest"
              label="Percentage of shipping rate to charge"
              [min]="-100"
              [max]="300">
            </pdq-input-arrows>
            <pdq-input-arrows
              [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
              tooltipIconColor="var(--warning-default)"
              width="275"
              widthSize="auto"
              formControlName="flatAmount"
              placeholder="For example: 1.00"
              [disabled]="!!runningABTest"
              [symbol]="shippingMethodCurrency$ | async | currencySign"
              label="Additional flat fee"
              precision="2">
            </pdq-input-arrows>
          </div>
        }

        @if (isLiveRateShippingMethod || isLiveRateETAFixedRate || isDomesticShippingMethod) {
          <pdq-checkbox
            *pdqHiddenInGlobaleMode
            [icon]="!!runningABTest ? 'more_information' : ''"
            [description]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
            tooltipIconColor="var(--warning-default)"
            labelSize="small"
            formControlName="calculatePackageDimension"
            [label]="
              isDomesticShippingMethod
                ? 'Calculate cost by package size & quantity'
                : 'Calculate rate in checkout by order\’s specific package size'
            "
            [secondaryLabel]="
              isDomesticShippingMethod ? '' : 'If unchecked, rate will be calculated by default weight based on size'
            "
            [disabled]="!!runningABTest"></pdq-checkbox>
        }

        @if (isLiveRateShippingMethod) {
          <pdq-radio-group
            formControlName="rateConversion"
            [disabled]="!!runningABTest"
            [tooltip]="!!runningABTest ? 'Disabled due to running A/B test' : ''"
            tooltipIconColor="var(--warning-default)"
            label="Set your rate conversion"
            labelSize="small">
            @for (customRates of DisplayRatesAs; track customRates) {
              <pdq-radio-button
                [value]="customRates.value"
                [checked]="
                  customRates.checked(formGroup.controls['rateConversion']?.value) || customRates.defaultChecked
                "
                [text]="customRates.text"
                [subText]="customRates.subText"
                [subTextNewLine]="true">
              </pdq-radio-button>
            }
          </pdq-radio-group>
        }
      </ng-container>

      @if (!isPickUpShippingMethod) {
        <fee-per-item [formGroup]="formGroup" [shippingId]="loadedValues?.id"></fee-per-item>

        <fee-per-weekday
          *pdqHiddenInGlobaleMode
          [formGroup]="formGroup"
          [shopCurrency]="shopCurrency"
          [loadedValues]="loadedValues"></fee-per-weekday>

        <fee-per-cart-value [formGroup]="formGroup" [loadedValues]="loadedValues"></fee-per-cart-value>
      }
    </div>
  </div>
</form>
